import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useId, useState } from "react";
import { TOUCH_TARGET, touchTargetQuery } from "_shared/_views/touch-target";
import type { GrantLevelOption } from "permissions/_views/grant-row/grant-row-model";
import { type GrantLevel, LEVEL_CONTROL_TITLE } from "permissions/_views/wording/permission-wording";

/**
 * A row's level: a chip at rest, one control when pressed.
 *
 * The control is a menu hanging from the chip at md and up and a sheet from the bottom of the screen
 * below it, where a menu beside a chip near the edge has no room for its second lines. Either saves
 * on choice. A level that cannot be changed from this row is the same chip, not pressable, so the
 * row reads the same whether or not the reader may change it.
 */

const LevelChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "removed" && prop !== "derived" })<{
  removed?: boolean;
  derived?: boolean;
}>(({ theme, removed, derived }) => ({
  maxWidth: "100%",
  ...(removed && { textDecoration: "line-through", color: theme.palette.text.secondary }),
  ...(derived && { borderStyle: "dashed" }),
  [touchTargetQuery(theme)]: {
    "&.MuiChip-clickable": { minHeight: TOUCH_TARGET },
  },
}));

const SheetActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2, 2),
  "& .MuiButton-root": { minHeight: TOUCH_TARGET },
}));

function isSameLevel(a: GrantLevel, b: GrantLevel): boolean {
  return a.accessLevel === b.accessLevel && a.scope === b.scope;
}

export default function LevelControl(
  props: Readonly<{
    text: string;
    current: GrantLevel;
    options: readonly GrantLevelOption[];
    /** The control's accessible name, "Access level of Proposals for Dana Ruiz". */
    ariaLabel: string;
    /** The sheet's heading below md, which has no row beside it to say which kind it is changing. */
    kindName: string;
    removed?: boolean;
    derived?: boolean;
    disabled?: boolean;
    onChange?: (level: GrantLevel) => void;
    /** Offered at the foot of the sheet, where the row's switch is out of reach behind it. */
    onRemove?: () => void;
  }>,
) {
  const { text, current, options, ariaLabel, kindName, removed, derived, disabled, onChange, onRemove } = props;
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up("md"));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const titleId = useId();

  const editable = options.length > 0 && onChange !== undefined;
  if (!editable) {
    return <LevelChip label={text} variant="outlined" removed={removed} derived={derived} />;
  }

  const close = () => setAnchor(null);
  const choose = (level: GrantLevel) => {
    close();
    if (!isSameLevel(level, current)) onChange(level);
  };

  const items = options.map((option) => (
    <MenuItem
      key={option.label}
      selected={isSameLevel(option.level, current)}
      onClick={() => choose(option.level)}
      sx={{ minHeight: TOUCH_TARGET, whiteSpace: "normal" }}
    >
      <ListItemText primary={option.label} secondary={option.help} />
    </MenuItem>
  ));

  return (
    <>
      <LevelChip
        label={text}
        variant="outlined"
        clickable
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-label={`${ariaLabel}: ${text}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        removed={removed}
        derived={derived}
      />
      {isWide ? (
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={close}
          slotProps={{ list: { "aria-labelledby": titleId, dense: false }, paper: { sx: { maxWidth: 360 } } }}
        >
          <ListSubheader id={titleId} disableSticky sx={{ lineHeight: 2.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {LEVEL_CONTROL_TITLE}
          </ListSubheader>
          {items}
        </Menu>
      ) : (
        <Drawer anchor="bottom" open={Boolean(anchor)} onClose={close} slotProps={{ paper: { sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12 } } }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="subtitle1" component="h2">
              {kindName}
            </Typography>
            <Typography id={titleId} variant="body2" color="text.secondary">
              {LEVEL_CONTROL_TITLE}
            </Typography>
          </Box>
          <MenuList aria-labelledby={titleId} autoFocusItem>
            {items}
          </MenuList>
          <SheetActions>
            {onRemove && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  close();
                  onRemove();
                }}
              >
                Remove access
              </Button>
            )}
            <Button sx={{ ml: "auto" }} onClick={close}>
              Done
            </Button>
          </SheetActions>
        </Drawer>
      )}
    </>
  );
}
